import { FunctionComponent } from 'react';
import {
  OnboardingCarePhasesPhaseOne,
  OnboardingCareSettingsPhaseOne,
  OnboardingCareSettingsPhaseTwo,
  OnboardingCareSettingsPhaseThree,
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesSummary,
  OnboardingFundingSources,
  OnboardingFundingCoverage,
  OnboardingDownloadReport,
  OnboardingOurPredictions,
  OnboardingPredictedCareNeeds,
  OnboardingOverview,
  OnboardingPolicyApplicationDashboard,
} from '@/pages';
import { PageLink } from '@/components';

import { Menu } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  clientHasPolicyFunding,
  clientHasAnnuityFunding,
  clientHasSelfFunding,
} from '@/pages/OnboardingFundingSources';
import { CareEnvironment, CarePhase, SessionType, isPolicyEligibleForApplication } from '@shared';

function isNestedSlide(slide: FunctionComponent<ClientIdProps>) {
  return (
    slide === OnboardingCareSettingsPhaseOne ||
    slide === OnboardingCareSettingsPhaseTwo ||
    slide === OnboardingCareSettingsPhaseThree ||
    slide === OnboardingFamilyCaregivingPhaseOne ||
    slide === OnboardingFamilyCaregivingPhaseTwo ||
    slide === OnboardingFamilyCaregivingPhaseThree
  );
}

function getFilteredSlides(
  slides: FunctionComponent<ClientIdProps>[],
  client: any,
): FunctionComponent<ClientIdProps>[] {
  let filteredSlides = slides;

  // Hide prediction slides for clients who have started LTC
  if (client.intakeSurvey.clientHasStartedLtc) {
    filteredSlides = filteredSlides.filter(
      slide => slide !== OnboardingOurPredictions && slide !== OnboardingPredictedCareNeeds,
    );
  }

  return filteredSlides;
}

function isSlideDisabled(
  slide: FunctionComponent<ClientIdProps>,
  index: number,
  selectedSlides: FunctionComponent<ClientIdProps>[],
  careEnvironmentSelections: Partial<Record<CarePhase, CareEnvironment | null>> | undefined,
  client: any,
) {
  for (let i = 0; i < index; i++) {
    const prevSlide = selectedSlides[i];

    if (
      prevSlide === OnboardingCarePhasesPhaseOne &&
      !careEnvironmentSelections?.[CarePhase.earlyCare]
    ) {
      if (slide === OnboardingCareSettingsPhaseOne) {
        return false;
      }
      return true;
    }

    if (
      prevSlide === OnboardingCareSettingsPhaseOne &&
      !careEnvironmentSelections?.[CarePhase.moderateCare]
    ) {
      if (slide === OnboardingCareSettingsPhaseTwo) {
        return false;
      }
      return true;
    }

    if (
      prevSlide === OnboardingCareSettingsPhaseTwo &&
      !careEnvironmentSelections?.[CarePhase.fullCare]
    ) {
      if (slide === OnboardingCareSettingsPhaseThree) {
        return false;
      }
      return true;
    }
  }

  const fundingSourcesIndex = selectedSlides.indexOf(OnboardingFundingSources);
  if (fundingSourcesIndex !== -1 && index > fundingSourcesIndex) {
    const fundingSourceAdded =
      clientHasPolicyFunding(client) ||
      clientHasAnnuityFunding(client) ||
      clientHasSelfFunding(client);

    if (
      (slide === OnboardingFundingCoverage || slide === OnboardingDownloadReport) &&
      !fundingSourceAdded
    ) {
      return true;
    }

    // Disable Application Review if no policies were added via shop insurance
    if (slide === OnboardingPolicyApplicationDashboard) {
      const ltcPolicies = Object.values(
        client.multipleFundingSources.ltcPolicy,
      ) as PolicyFundingSource[];
      const hasShopInsurancePolicy = ltcPolicies.some(ltcPolicy =>
        isPolicyEligibleForApplication(ltcPolicy),
      );
      if (!hasShopInsurancePolicy) {
        return true;
      }
    }
  }

  return false;
}

type NavigationMenuItemProps = {
  slide: FunctionComponent<ClientIdProps>;
  index: number;
  isActive: boolean;
  isCompleted: boolean;
  title: string;
  isNested: boolean;
  isDisabled: boolean;
  isMobile: boolean;
  clientId: string;
  onItemClick?: () => void;
};

function NavigationMenuItem({
  slide,
  index,
  isActive,
  isCompleted,
  title,
  isNested,
  isDisabled,
  isMobile,
  clientId,
  onItemClick,
}: NavigationMenuItemProps) {
  return (
    <PageLink
      key={index}
      to={slide}
      targetProps={{ clientId }}
      onClick={e => {
        if (isDisabled) {
          e.preventDefault();
          return;
        }
        onItemClick?.();
      }}
      className={cn(
        'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium',
        isMobile
          ? cn(
              isActive && 'bg-gray-100 text-gray-900',
              !isActive && !isDisabled && 'text-gray-700 opacity-75 hover:opacity-75',
              isDisabled && 'cursor-not-allowed text-gray-500',
              isNested && 'pl-6',
            )
          : cn(
              'group/item text-xs',
              isActive && 'text-gray-900',
              !isActive && !isDisabled && 'text-gray-700',
              isDisabled && 'cursor-not-allowed text-gray-500',
              isNested && 'pl-6',
            ),
      )}
    >
      <div
        className={cn(
          'h-[1px] bg-current',
          isNested ? 'w-[5px]' : 'w-[15px]',
          isActive ? 'opacity-100' : 'opacity-75',
        )}
      />
      <span
        className={cn(
          isMobile
            ? cn(
                isActive && 'opacity-100',
                !isActive && !isDisabled && 'opacity-75',
                isDisabled && 'opacity-50',
              )
            : cn(
                'transition-opacity duration-200',
                isActive && 'opacity-100',
                !isActive && !isDisabled && 'opacity-0 group-hover:opacity-75',
                isDisabled && 'opacity-0 group-hover:opacity-50',
              ),
        )}
      >
        {title}
      </span>
    </PageLink>
  );
}

type OnboardingNavigationMenuProps = {
  selectedSlides: FunctionComponent<ClientIdProps>[];
  relativeSlideIndex: number;
  slideTitles: Map<FunctionComponent<ClientIdProps>, string>;
  client: any;
  careEnvironmentSelections: Partial<Record<CarePhase, CareEnvironment | null>> | undefined;
  sessionType: SessionType;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  currentSlidePage: FunctionComponent<ClientIdProps>;
};

export function OnboardingNavigationMenu({
  selectedSlides,
  relativeSlideIndex,
  slideTitles,
  client,
  careEnvironmentSelections,
  sessionType,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  currentSlidePage,
}: OnboardingNavigationMenuProps) {
  const filteredSlides = getFilteredSlides(selectedSlides, client);
  const filteredRelativeSlideIndex = filteredSlides.indexOf(currentSlidePage);

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className={cn(
          'border-primary-100 bg-primary-50 hover:bg-primary-100 hover:text-primary-dark top-4.5 right-18 absolute z-50 flex shrink-0 items-center justify-center rounded-full border p-1.5 text-gray-700 md:right-32 xl:hidden',
          isMobileMenuOpen && 'hidden',
        )}
        aria-label="Toggle navigation menu"
        data-testid="onboarding-menu-button"
      >
        <Menu
          className="h-5 w-5 md:h-6 md:w-6"
          strokeWidth={1}
        />
      </button>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 xl:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        className={cn(
          'w-70 fixed inset-y-0 right-0 z-50 bg-white transition-transform duration-300 ease-in-out xl:hidden',
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex h-full flex-col overflow-y-auto p-4">
          <div className="mt-2 space-y-2">
            {filteredSlides.map((slide, index) => (
              <NavigationMenuItem
                key={index}
                slide={slide}
                index={index}
                isActive={index === filteredRelativeSlideIndex}
                isCompleted={index < filteredRelativeSlideIndex}
                title={slideTitles.get(slide) || `Step ${index + 1}`}
                isNested={isNestedSlide(slide)}
                isDisabled={isSlideDisabled(
                  slide,
                  index,
                  filteredSlides,
                  careEnvironmentSelections,
                  client,
                )}
                isMobile={true}
                clientId={client.clientId}
                onItemClick={() => setIsMobileMenuOpen(false)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Vertical Navigation (Desktop) */}
      <div className="fixed left-0 top-1/2 z-10 hidden -translate-y-1/2 xl:block">
        <div className="group flex flex-col justify-center rounded-r-lg bg-transparent p-4 transition-colors duration-200 hover:bg-white hover:shadow-lg">
          {filteredSlides.map((slide, index) => (
            <NavigationMenuItem
              key={index}
              slide={slide}
              index={index}
              isActive={index === filteredRelativeSlideIndex}
              isCompleted={index < filteredRelativeSlideIndex}
              title={slideTitles.get(slide) || `Step ${index + 1}`}
              isNested={isNestedSlide(slide)}
              isDisabled={isSlideDisabled(
                slide,
                index,
                filteredSlides,
                careEnvironmentSelections,
                client,
              )}
              isMobile={false}
              clientId={client.clientId}
            />
          ))}
        </div>
      </div>
    </>
  );
}
